import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { zoomCard } from "@/components/CardZoom";
import { toast } from "sonner";
import { X, Loader2, HandCoins } from "lucide-react";

const RARITY = {
  exclusive: { label: "EXCLUSIVE", cls: "border-pb-red/70 text-pb-red" },
  icon: { label: "ICON", cls: "border-white/70 text-white" },
  legendary: { label: "LEGENDARY", cls: "border-violet-400/70 text-violet-300" },
  elite: { label: "ELITE", cls: "border-amber-400/70 text-amber-300" },
  gem: { label: "GEM", cls: "border-sky-400/60 text-sky-300" },
  gold: { label: "GOLD", cls: "border-yellow-500/70 text-yellow-400" },
  holo: { label: "HOLO", cls: "border-fuchsia-400/60 text-fuchsia-300" },
  rare: { label: "RARE", cls: "border-sky-500/60 text-sky-400" },
  standard: { label: "STANDARD", cls: "border-zinc-600 text-zinc-400" },
  common: { label: "COMMON", cls: "border-stone-500/60 text-stone-400" },
};

const protectionChip = (c) => {
  const numbered = c.one_of_one || (c.edition_num && c.edition_total);
  if (c.slabbed) {
    const black = c.grade_label === "BULLPEN BLACK LABEL 10" && c.autographed;
    if (black && numbered) return { t: "★★ BLACK LABEL №", cls: "border-yellow-300 text-yellow-300 bg-gradient-to-r from-yellow-500/25 to-black" };
    if (black) return { t: "★ BLACK LABEL", cls: "border-yellow-300 text-yellow-300 bg-yellow-400/10" };
    if (c.autographed && numbered) return { t: "№ NUMBERED SLAB", cls: "border-amber-400/70 text-amber-300" };
    if (c.autographed) return { t: "GRADED + SIGNED", cls: "border-zinc-300/60 text-zinc-200" };
    return { t: "GRADED", cls: "border-zinc-400/50 text-zinc-300" };
  }
  if (c.protection === "toploader") return { t: "TOP LOADED · GRADE LOCKED", cls: "border-teal-400/60 text-teal-300" };
  if (c.protection === "sleeve") return { t: "SLEEVED", cls: "border-teal-400/40 text-teal-400" };
  return { t: "RAW", cls: "border-zinc-700 text-zinc-600" };
};

const VaultTile = ({ c, isMine }) => {
  const r = RARITY[c.rarity] || RARITY.standard;
  const [offering, setOffering] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const sendOffer = async () => {
    setBusy(true);
    try {
      await api.post(`/vault/copies/${c.id}/offer`, { amount: Number(amount) || 0 });
      toast.success(`Offer sent — ${c.owner_name || "the owner"} will be notified`);
      setOffering(false); setAmount("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className={`border p-2 flex flex-col gap-1.5 bg-pb-surface ${c.one_of_one ? "border-amber-400/70" : "border-pb-border"}`}
      data-testid={`pv-card-${c.id}`}>
      {c.slabbed ? (
        <div className="slab-case p-1.5 flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-1 bg-white/[0.07] border border-zinc-300/40 px-1.5 py-0.5">
            <span className="text-[6.5px] font-black uppercase tracking-widest text-zinc-100 truncate">Bullpen Graded</span>
            <span className="text-[10px] font-black text-white shrink-0">{c.grade}</span>
          </div>
          <div className="slab-window">
            <img src={c.url} alt={c.subject_name} loading="lazy" className="w-full aspect-[3/4] object-contain bg-black cursor-zoom-in"
              onClick={() => zoomCard(c.url, c.subject_name, c, c.game)} />
          </div>
        </div>
      ) : (
        <img src={c.url} alt={c.subject_name} loading="lazy" className="w-full aspect-[3/4] object-contain bg-black cursor-zoom-in"
          onClick={() => zoomCard(c.url, c.subject_name, null, c.game)} />
      )}
      <div className="flex items-center justify-between gap-1">
        <span className="text-[10px] font-semibold truncate">{c.subject_name}</span>
        <span className={`shrink-0 text-[7px] font-black uppercase tracking-widest px-1 py-0.5 border ${r.cls}`}>
          {c.rarity === "gem" && c.gem_label ? c.gem_label.split(" ")[0] : r.label}
        </span>
      </div>
      {(c.grade_label || c.edition_num) && (
        <div className="text-[7.5px] uppercase tracking-widest text-zinc-500 truncate">
          {c.grade_label}{c.edition_num && c.edition_total ? ` · #${c.edition_num}/${c.edition_total}` : ""}{c.one_of_one ? " · 1 OF 1" : ""}
        </div>
      )}
      {(() => { const p = protectionChip(c); return (
        <span data-testid={`pv-protection-${c.id}`}
          className={`self-start text-[6.5px] font-black uppercase tracking-widest px-1.5 py-0.5 border ${p.cls}`}>
          {p.t}
        </span>
      ); })()}
      {c.price > 0 && <div className="text-[9px] font-mono font-bold text-pb-cyan">Listed · {c.price} BLC</div>}
      {!isMine && (offering ? (
        <div className="flex gap-1">
          <input data-testid={`pv-offer-amount-${c.id}`} type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder="BLC" className="w-full min-w-0 bg-pb-elevated border border-pb-border px-1.5 py-1 text-[10px] font-mono outline-none focus:border-pb-cyan" />
          <button data-testid={`pv-offer-send-${c.id}`} onClick={sendOffer} disabled={busy || !(Number(amount) > 0)}
            className="shrink-0 text-[8px] font-black uppercase tracking-widest px-2 bg-pb-cyan text-black disabled:opacity-40">
            {busy ? "..." : "Send"}
          </button>
          <button onClick={() => setOffering(false)} className="shrink-0 text-zinc-500 hover:text-white"><X className="w-3 h-3" /></button>
        </div>
      ) : (
        <button data-testid={`pv-offer-btn-${c.id}`} onClick={() => setOffering(true)}
          className="w-full flex items-center justify-center gap-1 text-[8px] font-black uppercase tracking-widest py-1 border border-pb-cyan/40 text-pb-cyan hover:bg-pb-cyan hover:text-black transition-colors">
          <HandCoins className="w-2.5 h-2.5" /> Offer to Buy
        </button>
      ))}
    </div>
  );
};

export default function PlayerVaultModal({ playerId, playerName, onClose }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get(`/vault/players/${playerId}/collection`).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [playerId]);
  const isMine = user?.id === playerId;
  return (
    <div className="fixed inset-0 z-[90] bg-black/85 flex items-center justify-center p-3" onClick={onClose}>
      <div data-testid="player-vault-modal" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-pb-surface border border-pb-border p-5 animate-fade-up">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display font-black uppercase text-lg" data-testid="player-vault-title">{isMine ? "My Vault" : `${playerName}'s Vault`}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              {data ? `${data.copies.length} card${data.copies.length === 1 ? "" : "s"} held` : "Loading collection"}
              {!isMine && " · view only — make an offer if something catches your eye"}
            </div>
          </div>
          <button data-testid="player-vault-close" onClick={onClose}><X className="w-5 h-5 text-zinc-500 hover:text-white" /></button>
        </div>
        {error ? (
          <div className="text-sm text-zinc-500 text-center py-10">Couldn't load this vault — try again.</div>
        ) : !data ? (
          <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-pb-cyan animate-spin" /></div>
        ) : data.copies.length === 0 ? (
          <div className="text-sm text-zinc-500 text-center py-10" data-testid="player-vault-empty">
            {isMine ? "Your vault is empty — rip a pack to start collecting." : `${playerName} isn't holding any cards yet.`}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="player-vault-grid">
            {data.copies.map((c) => <VaultTile key={c.id} c={c} isMine={isMine} />)}
          </div>
        )}
      </div>
    </div>
  );
}
